/**
 * Categorical series palette for charts, per theme.
 *
 * Chart colours encode CATEGORY, so they are mostly theme-blind and the light
 * array below is the one that has always shipped, unchanged. Two of them were
 * not survivable on a dark card though:
 *
 *     #392CE5  2.13:1 on --color-bg-card  ->  #685EEB  3.51:1
 *     #962CE5  3.03:1                     ->  #A143E8  3.59:1
 *
 * 2.13:1 is why the second line in Response Time Trends was almost invisible in
 * dark, and why the indigo slices of the Alarms by Reason pie sank into the
 * card. Both were re-derived by holding hue and saturation and raising only
 * lightness until they cleared 3.5:1, which is a little above the 3:1 WCAG
 * floor for non-text graphics.
 *
 * Everything else is identical between the two arrays. Lifting a whole palette
 * toward white is the obvious mistake here: it collapses the distance between
 * neighbouring series. The closest pair in both arrays is #2C7BE5 vs #8989FF at
 * dE 18.6 -- unchanged, so these two edits added no collision.
 *
 * Chart.js and the LineTrendingChart wrapper both take plain colour arrays, so
 * this is a hook rather than CSS custom properties: reading a var() back out of
 * getComputedStyle after a theme switch returns stale values often enough to be
 * untrustworthy (see the verification-harness notes), whereas a hook re-renders
 * the chart with the right array for free.
 */

import { useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext.jsx';

export const CHART_SERIES_LIGHT = [
    '#2C7BE5', '#392CE5', '#05B2FF', '#A6C5F7', '#8989FF', '#D2DDEC',
    '#E52C7B', '#962CE5', '#E52CD8', '#E52C7B', '#E5392C', '#09BA00',
    '#0CFF00', '#8AFF85', '#AEFF00', '#F2FF00', '#A7B000',
];

export const CHART_SERIES_DARK = [
    '#2C7BE5', '#685EEB', '#05B2FF', '#A6C5F7', '#8989FF', '#D2DDEC',
    '#E52C7B', '#A143E8', '#E52CD8', '#E52C7B', '#E5392C', '#09BA00',
    '#0CFF00', '#8AFF85', '#AEFF00', '#F2FF00', '#A7B000',
];

/* The "Additional... (n)" bucket. Neutral grey reads on both grounds
   (6.62:1 dark, 3.1:1 light), so it needs no per-theme value. */
export const CHART_OTHER_COLOR = '#9CA3AF';

/** The series palette for the active theme. */
export function useChartSeries() {
    const { theme } = useTheme();
    return useMemo(
        () => (theme === 'dark' ? CHART_SERIES_DARK : CHART_SERIES_LIGHT),
        [theme],
    );
}
